import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Pilcrow,
  Link2,
  Unlink,
} from "lucide-react";

type BlockFormat = "p" | "h2" | "h3";

const exec = (command: string, value?: string) => {
  try {
    document.execCommand(command, false, value);
  } catch {
    // execCommand can throw in isolated environments; ignore formatting failure.
  }
};

const ToolbarButton = ({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="h-8 w-8 rounded-md flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className="w-px h-5 bg-gray-200 mx-1" aria-hidden="true" />;

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  error?: boolean;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter content...",
  minHeight = 320,
  error = false,
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    setIsEmpty(el.innerText.trim().length === 0);
    onChange(el.innerHTML);
  }, [onChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value;
      setIsEmpty(el.innerText.trim().length === 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBlock = (format: BlockFormat) => {
    exec("formatBlock", format);
    emit();
  };

  const setList = (command: "insertUnorderedList" | "insertOrderedList") => {
    exec(command);
    emit();
  };

  const setInline = (command: string) => {
    exec(command);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Enter the link URL (e.g. https://example.com/terms)");
    if (!url) return;
    let href = url.trim();
    if (href && !/^https?:\/\//i.test(href)) href = `https://${href}`;
    exec("createLink", href);
    emit();
  };

  const removeLink = () => {
    exec("unlink");
    emit();
  };

  return (
    <div
      className={`rounded-lg border bg-white transition-colors ${
        error
          ? "border-red-300 bg-red-50 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-200"
          : "border-gray-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500"
      }`}
    >
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <ToolbarButton title="Paragraph" onClick={() => setBlock("p")}>
          <Pilcrow size={15} />
        </ToolbarButton>
        <ToolbarButton title="Heading 1" onClick={() => setBlock("h2")}>
          <Heading1 size={15} />
        </ToolbarButton>
        <ToolbarButton title="Heading 2" onClick={() => setBlock("h3")}>
          <Heading2 size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton title="Bold" onClick={() => setInline("bold")}>
          <Bold size={15} />
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => setInline("italic")}>
          <Italic size={15} />
        </ToolbarButton>
        <ToolbarButton title="Underline" onClick={() => setInline("underline")}>
          <Underline size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton title="Bullet list" onClick={() => setList("insertUnorderedList")}>
          <List size={15} />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => setList("insertOrderedList")}>
          <ListOrdered size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton title="Insert link" onClick={insertLink}>
          <Link2 size={15} />
        </ToolbarButton>
        <ToolbarButton title="Remove link" onClick={removeLink}>
          <Unlink size={15} />
        </ToolbarButton>
      </div>

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          style={{ minHeight }}
          className="px-4 py-3 text-sm text-gray-800 focus:outline-none rounded-b-lg overflow-y-auto [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_a]:text-primary-600 [&_a]:underline"
        />
        {isEmpty && (
          <div className="pointer-events-none absolute left-4 top-3 text-sm text-gray-400">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;